import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiConstants from 'shared/api/apiConstants';
import { toast } from 'react-toastify';
import { useRootStore } from '~/app/contexts/RootStoreContext.jsx';
import { useInvalidateFiltersDistinctValues } from 'invoices/hooks/react-query/useInvoiceFilters';
import { STALE_TIME } from 'users/containers/Organization/consts';
import { handleError } from './helperFunctions';
import {
  createCostCenter,
  deleteDataAccessCostCenter,
  fetchCostCenters,
  fetchManagementCostCenterAccounts,
  fetchManagementCostCenters,
  fetchManagementCostCenterUnassignedAccounts,
  updateDataAccessCostCenter,
} from './apiCostCenters';

export const useCostCenters = (isDisabled = false) => {
  const { usersStore } = useRootStore();
  const userAccountKey = usersStore?.currDispUserAccountKey;
  const { invalidatePlainFilters } = useInvalidateFiltersDistinctValues();
  const queryClient = useQueryClient();
  const queryKey = [apiConstants.QUERY_KEYS.COST_CENTERS, userAccountKey];

  return {
    reset: () => queryClient.resetQueries({ queryKey }),
    fetchCostCentersData: (userKey) =>
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useQuery({
        queryKey: [...queryKey, userKey],
        queryFn: fetchCostCenters,
        onError: () =>
          toast.error('Error fetching cost centers data', {
            position: toast.POSITION.BOTTOM_RIGHT,
          }),
        enabled: !!userAccountKey,
      }),
    fetchManagementCostCenters: (isCustomersView, selectedCustomerDetails) =>
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useQuery({
        queryKey: [
          apiConstants.QUERY_KEYS.USER_MANAGEMENT_DATA_ACCESS_COST_CENTERS,
          userAccountKey,
          isCustomersView,
          selectedCustomerDetails,
        ],
        queryFn: fetchManagementCostCenters,
        enabled: !isDisabled,
        retry: false,
        staleTime: STALE_TIME,
        onError: handleError,
      }),
    fetchCostCenterUnassignedAccounts: ({ costCenterId, isCustomersView, selectedCustomer }) =>
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useQuery({
        queryKey: [
          apiConstants.QUERY_KEYS.USER_MANAGEMENT_DATA_ACCESS_COST_CENTERS,
          userAccountKey,
          costCenterId,
          'unassigned',
          isCustomersView,
          selectedCustomer,
        ],
        queryFn: () => fetchManagementCostCenterUnassignedAccounts(costCenterId),
        enabled: !isDisabled,
        retry: false,
        staleTime: STALE_TIME,
        onError: () =>
          toast.error('Error fetching cost center unassigned accounts data', {
            position: toast.POSITION.BOTTOM_RIGHT,
          }),
      }),
    fetchCostCenterAccountsData: (costCenterId) =>
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useQuery({
        queryKey: [apiConstants.QUERY_KEYS.USER_MANAGEMENT_DATA_ACCESS_COST_CENTERS, userAccountKey, costCenterId],
        queryFn: () => costCenterId && fetchManagementCostCenterAccounts(costCenterId),
        enabled: !isDisabled,
        retry: false,
        staleTime: STALE_TIME,
        onError: () =>
          toast.error('Error fetching cost center accounts data', {
            position: toast.POSITION.BOTTOM_RIGHT,
          }),
      }),
    createCostCenter: useMutation({
      mutationFn: ({ costCenter, accountsPayload }) => createCostCenter({ costCenter, accountsPayload }),
      onSuccess: async () => {
        await queryClient.resetQueries({
          queryKey: [apiConstants.QUERY_KEYS.USER_MANAGEMENT_DATA_ACCESS_COST_CENTERS, userAccountKey],
        });
        await queryClient.invalidateQueries({
          queryKey: [apiConstants.QUERY_KEYS.USER_MANAGEMENT_ROLES, userAccountKey],
        });
        await queryClient.invalidateQueries({
          queryKey: [apiConstants.QUERY_KEYS.COST_CENTERS, userAccountKey],
        });
        invalidatePlainFilters();
      },
      onError: handleError,
    }),
    updateDataAccessCostCenter: useMutation({
      mutationFn: ({ costCenterId, costCenterAccountsData }) =>
        updateDataAccessCostCenter({ costCenterId, costCenterAccountsData }),
      onSuccess: async () => {
        queryClient.invalidateQueries({
          queryKey: [apiConstants.QUERY_KEYS.USER_MANAGEMENT_DATA_ACCESS_COST_CENTERS, userAccountKey],
        });
        await queryClient.invalidateQueries({
          queryKey: [apiConstants.QUERY_KEYS.USER_MANAGEMENT_ROLES, userAccountKey],
        });
        await queryClient.invalidateQueries({
          queryKey: [apiConstants.QUERY_KEYS.COST_CENTERS],
        });
        await queryClient.invalidateQueries({
          queryKey: [apiConstants.QUERY_KEYS.RECOMMENDATIONS],
        });
        invalidatePlainFilters();
      },
      onError: handleError,
    }),
    deleteDataAccessCostCenter: useMutation({
      mutationFn: (costCenterId) => deleteDataAccessCostCenter(costCenterId),
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [apiConstants.QUERY_KEYS.USER_MANAGEMENT_DATA_ACCESS_COST_CENTERS, userAccountKey],
        });
        await queryClient.invalidateQueries({
          queryKey: [apiConstants.QUERY_KEYS.USER_MANAGEMENT_ROLES, userAccountKey],
        });
        invalidatePlainFilters();
      },
      onError: handleError,
    }),
  };
};
